import { useEffect, useRef } from 'react'
import { useStore } from 'react-redux'
import { getSocket } from '../../socket/socket'

const MAX_TARGETS = 20
const MAX_VALUE_TARGETS = 8
const ACTIVITY_DELAY = 450

const getBaseId = (value) => {
    const id = String(value || '')
    return id.includes('__') ? id.split('__')[0] : id
}

const getTargetIds = (scene = {}) => {
    const selected = Array.isArray(scene.selectedModelPaths)
        ? scene.selectedModelPaths.filter(Boolean)
        : []
    const values = selected.length
        ? selected
        : (scene.selectedModel ? [scene.selectedModel] : [])

    return [...new Set(values.map((value) => String(value || '')).filter(Boolean))]
        .slice(0, MAX_TARGETS)
}

const copyVector = (value, fallback = [0, 0, 0]) => {
    if (!Array.isArray(value)) return [...fallback]

    return [
        Number(value[0]) || 0,
        Number(value[1]) || 0,
        Number(value[2]) || 0,
    ]
}

const copyTargetVectors = (source = {}, targetIds = [], fallback = [0, 0, 0]) => {
    const result = {}

    targetIds.slice(0, MAX_VALUE_TARGETS).forEach((id) => {
        result[id] = copyVector(source?.[id], fallback)
    })

    return result
}

const arraysEqual = (left = [], right = []) => (
    left.length === right.length && left.every((value, index) => value === right[index])
)

const getHistoryState = (scene = {}) => {
    const past = Array.isArray(scene.history?.past) ? scene.history.past : []
    const future = Array.isArray(scene.history?.future) ? scene.history.future : []
    const last = past[past.length - 1] || null

    return {
        pastLength: past.length,
        futureLength: future.length,
        lastId: String(last?.historyId || ''),
        lastLabel: String(last?.historyLabel || ''),
    }
}

const buildCompactSnapshot = (scene = {}) => {
    const targetIds = getTargetIds(scene)
    const history = getHistoryState(scene)

    return {
        targetIds,
        selectedModel: String(scene.selectedModel || ''),
        selectedModelPathsRef: scene.selectedModelPaths,
        historyRef: scene.history,
        modelPathsRef: scene.modelPaths,
        modelPathCount: Array.isArray(scene.modelPaths) ? scene.modelPaths.length : 0,
        selectedModelsRef: scene.selectedModels,
        modifierPreviewsRef: scene.modifierPreviews,
        hiddenModelsRef: scene.hiddenModels,
        monumentAssembliesRef: scene.monumentAssemblies,
        positionsRef: scene.positions,
        rotationsRef: scene.rotations,
        scalesRef: scene.scales,
        transformMode: String(scene.transformMode || ''),
        positions: copyTargetVectors(scene.positions, targetIds, [0, 0, 0]),
        rotations: copyTargetVectors(scene.rotations, targetIds, [0, 0, 0]),
        scales: copyTargetVectors(scene.scales, targetIds, [1, 1, 1]),
        ...history,
    }
}


const hasRelevantSceneChange = (previous, scene = {}) => {
    if (!previous) return true

    return previous.selectedModel !== String(scene.selectedModel || '') ||
        previous.selectedModelPathsRef !== scene.selectedModelPaths ||
        previous.modelPathsRef !== scene.modelPaths ||
        previous.selectedModelsRef !== scene.selectedModels ||
        previous.modifierPreviewsRef !== scene.modifierPreviews ||
        previous.hiddenModelsRef !== scene.hiddenModels ||
        previous.monumentAssembliesRef !== scene.monumentAssemblies ||
        previous.positionsRef !== scene.positions ||
        previous.rotationsRef !== scene.rotations ||
        previous.scalesRef !== scene.scales ||
        previous.transformMode !== String(scene.transformMode || '') ||
        previous.historyRef !== scene.history
}

const hasMirrorSignChange = (previous = {}, current = {}) => (
    Object.keys(current).some((id) => {
        const before = previous[id] || []
        const after = current[id] || []

        return [0, 1, 2].some((axis) => {
            const beforeValue = Number(before[axis])
            const afterValue = Number(after[axis])

            return Number.isFinite(beforeValue) &&
                Number.isFinite(afterValue) &&
                beforeValue !== 0 &&
                afterValue !== 0 &&
                Math.sign(beforeValue) !== Math.sign(afterValue)
        })
    })
)

const getObjectName = (scene, path) => {
    const rawPath = String(path || '')
    const custom = scene.customObjects?.[rawPath]

    if (custom?.name) return custom.name

    const baseId = getBaseId(rawPath)
    const meta = scene.productsMetaById?.[baseId] || {}
    const baseName = meta.fullName ||
        meta.name ||
        (meta.article ? `Арт. ${meta.article}` : '') ||
        baseId
    const duplicateIndex = rawPath.includes('__')
        ? Number(rawPath.split('__')[1] || 0)
        : 0

    return duplicateIndex > 0 ? `${baseName} — копия ${duplicateIndex}` : baseName
}

const classifyActivity = (previous, current) => {
    const changed = []

    if (previous.selectedModel !== current.selectedModel || !arraysEqual(previous.targetIds, current.targetIds)) {
        changed.push('selection')
    }
    if (previous.modelPathsRef !== current.modelPathsRef) changed.push('modelPaths')
    if (previous.selectedModelsRef !== current.selectedModelsRef) changed.push('selectedModels')
    if (previous.modifierPreviewsRef !== current.modifierPreviewsRef) changed.push('modifierPreviews')
    if (previous.hiddenModelsRef !== current.hiddenModelsRef) changed.push('hiddenModels')
    if (previous.monumentAssembliesRef !== current.monumentAssembliesRef) changed.push('monumentAssemblies')
    if (previous.scalesRef !== current.scalesRef) changed.push('scales')
    if (previous.rotationsRef !== current.rotationsRef) changed.push('rotations')
    if (previous.positionsRef !== current.positionsRef) changed.push('positions')
    if (previous.transformMode !== current.transformMode) changed.push('transformMode')

    const historyPastDecreased = current.pastLength < previous.pastLength
    const historyRedoDetected = (
        current.pastLength > previous.pastLength &&
        current.futureLength < previous.futureLength
    )
    const historyLabelChanged = current.lastId && current.lastId !== previous.lastId
    const historyLabel = current.lastLabel.toLowerCase()

    if (historyPastDecreased) {
        return { type: 'undo', label: 'Отменил последнее действие', changed }
    }

    if (historyRedoDetected) {
        return { type: 'redo', label: 'Вернул отменённое действие', changed }
    }

    if (historyLabelChanged && historyLabel.includes('зеркальн')) {
        return { type: 'mirror', label: 'Создал зеркальную копию', changed }
    }

    if (historyLabelChanged && historyLabel.includes('массив')) {
        return { type: 'array', label: 'Применил массив', changed }
    }

    if (historyLabelChanged && historyLabel.includes('количество')) {
        return {
            type: 'quantity',
            label: `Изменил количество объектов: ${previous.modelPathCount} → ${current.modelPathCount}`,
            changed,
        }
    }

    if (changed.includes('modifierPreviews')) {
        return { type: 'array', label: 'Настраивает или применяет массив', changed }
    }

    if (changed.includes('selectedModels') || changed.includes('modelPaths')) {
        const quantityChanged = current.modelPathCount !== previous.modelPathCount

        return {
            type: quantityChanged ? 'quantity' : 'objects',
            label: quantityChanged
                ? `Изменил количество объектов: ${previous.modelPathCount} → ${current.modelPathCount}`
                : 'Изменил состав объектов',
            changed,
        }
    }

    if (changed.includes('monumentAssemblies')) {
        return { type: 'monument_assembly', label: 'Изменил комплект памятника', changed }
    }

    if (changed.includes('hiddenModels')) {
        return { type: 'visibility', label: 'Изменил видимость объекта', changed }
    }

    if (changed.includes('scales')) {
        const mirrorDetected = hasMirrorSignChange(previous.scales, current.scales)

        return {
            type: mirrorDetected ? 'mirror' : 'scale',
            label: mirrorDetected ? 'Применил зеркало' : 'Изменил масштаб',
            changed,
        }
    }

    if (changed.includes('rotations')) {
        return { type: 'rotate', label: 'Повернул объект', changed }
    }

    if (changed.includes('positions')) {
        return { type: 'move', label: 'Переместил объект', changed }
    }

    if (changed.includes('selection')) {
        return { type: 'selection', label: 'Выбрал объект', changed }
    }

    if (changed.includes('transformMode')) {
        return { type: 'tool', label: 'Переключил инструмент редактирования', changed }
    }

    return null
}

const buildActivityPayload = ({
    scene,
    current,
    classification,
    guestToken,
    pageSessionId,
}) => {
    const targetIds = current.targetIds
    const targetNames = targetIds.map((path) => getObjectName(scene, path)).filter(Boolean)
    const values = {}

    targetIds.slice(0, MAX_VALUE_TARGETS).forEach((id) => {
        if (classification.type === 'move') values[id] = current.positions[id] || null
        if (classification.type === 'rotate') {
            values[id] = (current.rotations[id] || []).map((value) => (
                Math.round((Number(value) || 0) * 1800 / Math.PI) / 10
            ))
        }
        if (classification.type === 'scale' || classification.type === 'mirror') {
            values[id] = current.scales[id] || null
        }
        if (classification.type === 'visibility') {
            values[id] = !!scene.hiddenModels?.[id]
        }
    })

    const activeArray = scene.modifierPreviews?.array || null
    const baseLabel = classification.label
    const label = targetNames.length && !['quantity', 'undo', 'redo'].includes(classification.type)
        ? `${baseLabel}: ${targetNames.slice(0, 2).join(', ')}`
        : baseLabel

    return {
        guestToken,
        pageSessionId,
        type: classification.type,
        label,
        targetIds,
        meta: {
            changed: classification.changed,
            targetNames,
            values,
            objectCount: current.modelPathCount,
            transformMode: current.transformMode || null,
            monumentAssemblyCount: Object.keys(scene.monumentAssemblies || {}).length,
            array: activeArray ? {
                sourceId: activeArray.sourceId || null,
                count: Number(activeArray.count || 0),
                relative: activeArray.relative !== false,
                offset: activeArray.offset || null,
            } : null,
        },
    }
}

const SceneGuestActivityBridge = ({
    enabled,
    guestToken,
    visitorId,
    grantId,
    pageSessionId,
    resetKey,
    onDirty,
}) => {
    const store = useStore()
    const baselineRef = useRef(null)
    const frameRef = useRef(null)
    const timerRef = useRef(null)
    const pendingRef = useRef(null)
    const suppressUntilRef = useRef(0)
    const lastEmitAtRef = useRef(0)
    const onDirtyRef = useRef(onDirty)

    useEffect(() => {
        onDirtyRef.current = onDirty
    }, [onDirty])

    useEffect(() => {
        if (!enabled || !guestToken || !visitorId || !grantId) {
            baselineRef.current = null
            return undefined
        }

        suppressUntilRef.current = Date.now() + 1400
        lastEmitAtRef.current = 0
        baselineRef.current = buildCompactSnapshot(store.getState().scene || {})

        const clearScheduled = () => {
            if (frameRef.current !== null && typeof window !== 'undefined') {
                window.cancelAnimationFrame(frameRef.current)
                frameRef.current = null
            }

            if (timerRef.current !== null && typeof window !== 'undefined') {
                window.clearTimeout(timerRef.current)
                timerRef.current = null
            }
        }

        const flushPending = () => {
            timerRef.current = null
            const payload = pendingRef.current
            pendingRef.current = null

            if (payload) {
                lastEmitAtRef.current = Date.now()
                getSocket().emit('sceneGuest:activity', payload)
            }
        }

        const processLatestState = () => {
            frameRef.current = null

            const scene = store.getState().scene || {}
            const previous = baselineRef.current

            if (!hasRelevantSceneChange(previous, scene)) return

            const current = buildCompactSnapshot(scene)
            baselineRef.current = current

            if (!previous || Date.now() < suppressUntilRef.current) return

            const classification = classifyActivity(previous, current)
            if (!classification) return

            if (!['selection', 'tool'].includes(classification.type)) {
                onDirtyRef.current?.()
            }

            pendingRef.current = buildActivityPayload({
                scene,
                current,
                classification,
                guestToken,
                pageSessionId,
            })

            const elapsed = Date.now() - lastEmitAtRef.current
            const wait = Math.max(0, ACTIVITY_DELAY - elapsed)

            if (wait === 0) {
                flushPending()
            } else if (timerRef.current === null) {
                timerRef.current = window.setTimeout(flushPending, wait)
            }
        }

        const unsubscribe = store.subscribe(() => {
            if (frameRef.current !== null || typeof window === 'undefined') return
            frameRef.current = window.requestAnimationFrame(processLatestState)
        })

        return () => {
            unsubscribe()
            clearScheduled()
            pendingRef.current = null
        }
    }, [enabled, grantId, guestToken, pageSessionId, resetKey, store, visitorId])

    return null
}

export default SceneGuestActivityBridge
